import { Button } from '@jeraisonne/ui';
import { Check, RotateCcw } from 'lucide-react';
import { DecouverteProofView } from '@/features/decouverte/decouverte-proofs';

/** Ce qu'un compte gratuit donne tout de suite (aucun prix : on vend d'abord l'inscription). */
export const DEMO_FREE_PERKS: readonly string[] = [
  'Un exercice gratuit sur chaque SKILLCARD',
  'Ton diagnostic de programme, en 5 minutes',
  'Le défi Live School du dimanche',
  'Tes HORA et ta progression, gardés carte par carte',
];

/**
 * Fin de la démo sans compte (`/demo`, `/demo/exercise/$id`) : on a montré le
 * moteur (chaque ligne vérifiée, des HORA à la clé) — on vend maintenant
 * l'INSCRIPTION GRATUITE, pas une offre payante. Pas de prix ici : un prix
 * avant même le compte fait fuir, et sur iOS / Android ce serait un appel à
 * l'achat hors magasin. Les offres se découvrent une fois connecté.
 *
 * Toujours sombre (bleu nuit), comme l'accueil `/decouverte`.
 */
export function DemoEndScreen({
  onSignup,
  onRestart,
  restartLabel = 'Essayer une autre classe',
  onLogin,
}: {
  onSignup: () => void;
  onRestart: () => void;
  restartLabel?: string;
  onLogin: () => void;
}) {
  return (
    <section
      aria-labelledby="demo-end-title"
      className="relative mx-auto flex min-h-dvh w-full max-w-xl flex-col bg-[#061433] px-5 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1.5rem)] sm:min-h-0 sm:rounded-3xl sm:py-10"
      data-testid="demo-end"
    >
      <div className="flex justify-center">
        <DecouverteProofView proof={{ kind: 'cards', chip: 'Tes SKILLCARDS t’attendent' }} />
      </div>

      <p className="mt-6 text-xs font-extrabold uppercase tracking-[0.16em] text-[#f7d068]">
        Démo terminée
      </p>
      <h1
        id="demo-end-title"
        className="font-display mt-1.5 text-[1.9rem] font-extrabold leading-[1.08] text-white"
      >
        Tu viens de raisonner pas à pas. <span className="text-[#7fb6ff]">Garde le rythme.</span>
      </h1>
      <p className="mt-2 text-[15px] leading-snug text-white/80">
        Crée ton compte gratuit : tes HORA comptent pour de vrai, sur tes SKILLCARDS.
      </p>

      <ul className="mt-5 space-y-2">
        {DEMO_FREE_PERKS.map((perk) => (
          <li key={perk} className="flex items-start gap-2.5 text-[15px] text-white">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3ddc97] text-[#06331f]">
              <Check aria-hidden className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            {perk}
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-8">
        <Button
          variant="gold"
          size="lg"
          fullWidth
          onClick={onSignup}
          className="min-h-12 active:scale-[0.98]"
        >
          Créer mon compte gratuit
        </Button>
        <p className="mt-2 text-center text-xs text-white/60">
          Sans carte bancaire, sans mot de passe : un code par email.
        </p>
        <div className="mt-2 flex items-center justify-center gap-1 text-sm">
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex min-h-11 items-center gap-1.5 px-2 font-semibold text-[#9cc8ff] transition-colors hover:text-white"
          >
            <RotateCcw aria-hidden className="h-3.5 w-3.5" />
            {restartLabel}
          </button>
          <span aria-hidden className="text-white/30">
            ·
          </span>
          <button
            type="button"
            onClick={onLogin}
            className="min-h-11 px-2 text-white/70 transition-colors hover:text-white"
          >
            Se connecter
          </button>
        </div>
      </div>
    </section>
  );
}
